import { ImageProps } from '../../client/components/image/image.js'
import { TagProps } from '../../client/components/tag/tag.js'
import { Customer } from '../../client/models/customer.js'
import { ClaimStatus, ClaimType, LisaAndAgreementType } from '../enums.js'
import { ErrorInSubscriptionDataSimple } from '../errors/subscription-errors.js'
import { isValidUrl } from '../helpers.js'
import { translate } from '../i18n.js'
import { CalculationResponse, CalculationResult, SubscriptionClaimDTO, SubscriptionProductDTO } from '../types.js'
import { unformatLisaAndNumber } from '../utils/maths.js'
import { formatMonthlyPrice, getPlanDurationInMonths } from '../utils/utils.js'
import { SubscriptionProductDiscounts } from './common-subscription-product.js'
import { SubscriptionProductPlan } from './subscription-product-plan.js'
import { SubscriptionProductService } from './subscription-product-service.js'

export class CommonSubscriptionLisaAndProduct {
    readonly data: SubscriptionProductDTO
    readonly currencySymbol: string
    readonly locale: string
    public priceData: CalculationResult
    public priceDatas: CalculationResponse
    readonly plan: SubscriptionProductPlan

    public possiblePlans: SubscriptionProductPlan[]
    public customer?: Customer

    constructor(
        product: SubscriptionProductDTO,
        currencySymbol: string,
        locale: string,
        priceDatas: CalculationResponse,
        currentPlan: SubscriptionProductPlan,
    ) {
        this.data = product
        this.currencySymbol = currencySymbol
        this.locale = locale
        this.priceDatas = priceDatas

        const targetPlan = currentPlan.code === 'FLEX' ? LisaAndAgreementType.FLEXIBLE : LisaAndAgreementType.FIXED
        this.priceData = priceDatas.filter((result) => {
            return result.variation_type === targetPlan
        })[0]

        this.possiblePlans =
            this.data.possiblePlans?.map(
                (planPrice) =>
                    new SubscriptionProductPlan(
                        planPrice,
                        this.locale,
                        getPlanDurationInMonths(
                            planPrice.plan?.durationInMonths || 0,
                            planPrice.plan?.code,
                            planPrice.country?.code,
                        ),
                    ),
            ) || []

        if (!product.planPrice) {
            throw new ErrorInSubscriptionDataSimple('C2')
        }

        this.plan = new SubscriptionProductPlan(
            product.planPrice,
            this.locale,
            getPlanDurationInMonths(
                product.planPrice.plan?.durationInMonths || 0,
                product.planPrice.plan?.code,
                product.planPrice.country?.code,
            ),
        )
    }

    get policyProvider() {
        return this.data.policyProvider
    }

    get description() {
        return this.data.product?.description || ''
    }

    get imei() {
        return this.data.productImei
    }

    get uuid() {
        return this.data.uuid
    }

    get baseSubscriptionCostGross() {
        return unformatLisaAndNumber(this.priceData.stock_monthly_price)
    }

    get displayedPrice() {
        return formatMonthlyPrice(this.displayedPriceNumber, this.currencySymbol, this.locale)
    }

    get totalMonthlyDiscount() {
        return unformatLisaAndNumber(this.priceData.total_monthly_discount)
    }

    get totalMonthlyGenericDiscount() {
        return unformatLisaAndNumber(this.priceData.total_monthly_discount - this.priceData.trade_in_discount)
    }

    get totalMonthlyTradeInDiscount() {
        return unformatLisaAndNumber(this.priceData.trade_in_discount)
    }

    get discountDuration() {
        return this.priceData.total_duration
    }

    get services() {
        return this.data.services?.map((service) => new SubscriptionProductService(service, this.currencySymbol)) || []
    }

    get planDuration() {
        return this.plan.duration
    }

    get serviceDiscounts() {
        const discounts: SubscriptionProductDiscounts = {
            general: 0,
            ignoreVat: 0,
        }

        this.services.forEach((service) => {
            if (service.discountAffectsVat) {
                discounts.general += service.discountNumber
            } else {
                discounts.ignoreVat += service.discountNumber
            }
        })

        return discounts
    }

    get defaultDiscount() {
        if (!this.data.discount) {
            return 0
        }

        const discounts: SubscriptionProductDiscounts = this.serviceDiscounts

        return this.data.discount - (discounts.general + discounts.ignoreVat)
    }

    get sku() {
        return this.data.product?.sku
    }

    get minimalTotalCost() {
        // Is not used for Lisa& products
        return 0
    }

    get name() {
        return this.data.product?.translation?.name || this.data.product?.sku || ''
    }

    get displayedPriceNumber() {
        return unformatLisaAndNumber(this.priceData.price_after_discounts)
    }

    get claimCurrent(): SubscriptionClaimDTO | undefined {
        const activeClaims = this.data.claims?.filter(
            (claim) =>
                ![ClaimStatus.COMPLETED, ClaimStatus.DECLINED, ClaimStatus.HANDLEDBYUPGRADE].includes(
                    claim.claimStatus,
                ) && claim.claimType !== ClaimType.AUTOMATED_CANCEL,
        )

        return activeClaims?.[0]
    }

    get claims(): SubscriptionClaimDTO[] {
        return this.data.claims || []
    }

    imageProps(width = 40): ImageProps | undefined {
        const url = isValidUrl(this.data.product?.imageUrl)

        if (url) {
            return {
                role: 'presentation',
                srcset: `${url.toString()} ${width}w, ${url.toString()} ${width * 2}w`,
                width,
            }
        }
    }

    get claimStatusTagProps(): TagProps | undefined {
        if (!this.data.claims?.[0]?.claimStatus) {
            return undefined
        }

        const translationOptions = {
            lng: this.locale,
        }

        const claimStatusMap = {
            // CANCEL CLAIMS
            [ClaimStatus.PENDING + ClaimType.CANCEL]: {
                text: translate('subscription.cancel.claimStatus.pending', translationOptions),
            },
            [ClaimStatus.WAITINGFORPAYMENT + ClaimType.CANCEL]: {
                text: translate('subscription.cancel.claimStatus.waitingForPayment', translationOptions),
            },
            [ClaimStatus.DEVICERECEIVED + ClaimType.CANCEL]: {
                text: translate('subscription.cancel.claimStatus.deviceReceived', translationOptions),
            },
            [ClaimStatus.DEVICERECEIVEDDAMAGED + ClaimType.CANCEL]: {
                text: translate('subscription.cancel.claimStatus.deviceReceivedDamaged', translationOptions),
            },
            [ClaimStatus.APPROVED + ClaimType.CANCEL]: {
                text: translate('subscription.cancel.claimStatus.approved', translationOptions),
            },
            [ClaimStatus.DECLINED + ClaimType.CANCEL]: {
                text: translate('subscription.cancel.claimStatus.declined', translationOptions),
            },
            [ClaimStatus.COMPLETED + ClaimType.CANCEL]: {
                text: translate('subscription.cancel.claimStatus.completed', translationOptions),
            },
            [ClaimStatus.RETURNKITSENT + ClaimType.CANCEL]: {
                text: translate('subscription.cancel.claimStatus.sent', translationOptions),
            },

            // Automatic cancel claims
            [ClaimStatus.PENDING + ClaimType.AUTOMATED_CANCEL]: {
                text: translate('subscription.cancel.claimStatus.pending', translationOptions),
            },
            [ClaimStatus.WAITINGFORPAYMENT + ClaimType.AUTOMATED_CANCEL]: {
                text: translate('subscription.cancel.claimStatus.waitingForPayment', translationOptions),
            },
            [ClaimStatus.DEVICERECEIVED + ClaimType.AUTOMATED_CANCEL]: {
                text: translate('subscription.cancel.claimStatus.deviceReceived', translationOptions),
            },
            [ClaimStatus.DEVICERECEIVEDDAMAGED + ClaimType.AUTOMATED_CANCEL]: {
                text: translate('subscription.cancel.claimStatus.deviceReceivedDamaged', translationOptions),
            },
            [ClaimStatus.APPROVED + ClaimType.AUTOMATED_CANCEL]: {
                text: translate('subscription.cancel.claimStatus.approved', translationOptions),
            },
            [ClaimStatus.DECLINED + ClaimType.AUTOMATED_CANCEL]: {
                text: translate('subscription.cancel.claimStatus.declined', translationOptions),
            },
            [ClaimStatus.COMPLETED + ClaimType.AUTOMATED_CANCEL]: {
                text: translate('subscription.cancel.claimStatus.completed', translationOptions),
            },
            [ClaimStatus.HANDLEDBYUPGRADE + ClaimType.AUTOMATED_CANCEL]: {
                text: translate('subscription.cancel.claimStatus.completed', translationOptions),
            },
            [ClaimStatus.RETURNKITSENT + ClaimType.AUTOMATED_CANCEL]: {
                text: translate('subscription.cancel.claimStatus.sent', translationOptions),
            },

            // SWAP CLAIMS
            [ClaimStatus.PENDING + ClaimType.SWAP]: {
                text: translate('subscription.swap.claimStatus.pending', translationOptions),
            },
            [ClaimStatus.WAITINGFORPAYMENT + ClaimType.SWAP]: {
                text: translate('subscription.swap.claimStatus.waitingForPayment', translationOptions),
            },
            [ClaimStatus.PAYMENTERROR + ClaimType.SWAP]: {
                text: translate('subscription.swap.claimStatus.waitingForPayment', translationOptions),
            },
            [ClaimStatus.PAYMENTRECEIVED + ClaimType.SWAP]: {
                text: translate('subscription.swap.claimStatus.paymentReceived', translationOptions),
            },
            [ClaimStatus.DEVICERECEIVED + ClaimType.SWAP]: {
                text: translate('subscription.swap.claimStatus.deviceReceived', translationOptions),
            },
            [ClaimStatus.DEVICERECEIVEDDAMAGED + ClaimType.SWAP]: {
                text: translate('subscription.swap.claimStatus.deviceReceivedDamaged', translationOptions),
            },
            [ClaimStatus.APPROVED + ClaimType.SWAP]: {
                text: translate('subscription.swap.claimStatus.approved', translationOptions),
            },
            [ClaimStatus.DECLINED + ClaimType.SWAP]: {
                text: translate('subscription.swap.claimStatus.declined', translationOptions),
            },
            [ClaimStatus.COMPLETED + ClaimType.SWAP]: {
                text: translate('subscription.swap.claimStatus.completed', translationOptions),
            },
            [ClaimStatus.RETURNKITSENT + ClaimType.SWAP]: {
                text: translate('subscription.swap.claimStatus.sent', translationOptions),
            },
        }

        const props = claimStatusMap[this.data.claims[0].claimStatus + this.data.claims[0].claimType]

        if (!props) {
            return undefined
        }

        return {
            className: 'tag--black',
            ...props,
        }
    }

    get nickname() {
        if (this.data.nickname) {
            return this.data.nickname
        } else if (this.customer) {
            return translate('portal.products.nickname', {
                lng: this.locale,
                firstName: this.customer.firstName,
            })
        }

        return this.name
    }

    set nickname(value: string) {
        this.data.nickname = value
    }

    get hasNickname(): boolean {
        return !!this.data.nickname
    }
}
